import ChangePricingModelDialog from "./ChangePricingModelDialog";
import RpiCell from "./RpiCell";
import { useRpiCalculation } from "./useRpiCalculation";
import {
  ErrorOutline as ErrorIcon,
  MoreVert as MoreIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  History as PriorDataIcon,
} from "@mui/icons-material";
import {
  Box,
  Button as MuiButton,
  Fade,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Badge,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Switch,
  FormControlLabel,
  Autocomplete,
  TextField,
  FormControl,
  Select,
  Divider,
  InputLabel,
} from "@mui/material";
import * as dates from "common/dates";
import * as numbers from "common/numbers";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import StatusBadgeV2 from "components/common/StatusBadgeV2";
import StatusSelect from "components/common/StatusSelectWithChecks";
import Visible from "components/common/Visible";
import { usePricing } from "components/customPricing/dnfModel1/pricingHooks";
import FollowedPolicyTableSelect from "components/layers/FollowedPolicyTableSelect";
import * as config from "config";
import * as labels from "config.labels";
import * as layerSupport from "domain/layerSupport";
import { NONE_PAPER_PROVIDER } from "domain/layerSupport";
import * as programPricingResolver from "domain/programPricingResolver";
import * as _ from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TransitionGroup } from "react-transition-group";
import * as sovService from "services/sovService";
import * as staticDataService from "services/staticDataService";
import * as SOVStore from "store/SOV";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as analyticsStore from "store/analytics";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programPricingSlice from "store/programPricing";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as stateSelectors from "store/selectors/stateSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";
import * as utils from "utils";

const FADE_TIMEOUT = 300;

const getPolicyFormName = (code) => {
  const policyForm = config.POLICY_FORMS.find((form) => form.code === code);
  return policyForm ? policyForm.name : code;
};

const getValue = (layer, field, calculatedValues, programPricing) => {
  if (field.value)
    return field.value({ layer, programPricing, calculatedValues });
  let rootData;
  switch (field.root) {
    case "programPricing":
      rootData = programPricing?.layers?.[layer.id];
      break;
    case "layer":
      rootData = layer;
      break;
    default:
      rootData = calculatedValues?.values?.layers?.[layer.id];
      break;
  }
  return rootData?.[field.key] ?? "";
};

const FOLLOWED_POLICY_TAG = "<DnfLayerPricing>.layerPricing.followedPolicy";

const buildDataFromFields = (
  fields,
  layers,
  calculatedValues,
  programPricing
) => {
  return layers.reduce((acc, layer) => {
    const dataForLayer = fields.reduce((layerAcc, field) => {
      const value = getValue(layer, field, calculatedValues, programPricing);
      return { ...layerAcc, [field.key]: value };
    }, {});
    return { ...acc, [layer.id]: dataForLayer };
  }, {});
};

const getCellStyle = (layerId, highlightedLayers, isAnyHighlighted) => {
  if (highlightedLayers.includes(layerId)) {
    return {
      borderLeft: "1px solid orange",
      borderRight: "1px solid orange",
    };
  } else if (!isAnyHighlighted) {
    return {};
  }
  return {
    opacity: 0.3,
  };
};

const ActionMenu = ({
  showAdditionalInputs,
  setShowAdditionalInputs,
  onOpenChangePricingModel,
}) => {
  const anchorRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={"menu"}>
      <IconButton
        ref={anchorRef}
        onClick={() => setIsOpen(!isOpen)}
        variant={"text"}
        className={"toggle"}
        style={{ marginLeft: "-15px" }}
      >
        <MoreIcon />
      </IconButton>

      <Menu
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={(_, reason) => {
          if (reason === "backdropClick") setIsOpen(!isOpen);
        }}
      >
        <MenuItem
          onClick={() => {
            setShowAdditionalInputs(!showAdditionalInputs);
            setIsOpen(!isOpen);
          }}
        >
          <ListItemText>
            {showAdditionalInputs ? "Hide" : "Show"}
            {" Additional Inputs"}
          </ListItemText>
        </MenuItem>
        <Visible
          byTag={"<DnfLayerPricing>.layerPricing.menu.changePricingModel"}
          defaultVisible={false}
        >
          <MenuItem
            onClick={() => {
              onOpenChangePricingModel();
              setIsOpen(!isOpen);
            }}
          >
            <ListItemText>{"Change Pricing Model"}</ListItemText>
          </MenuItem>
        </Visible>
      </Menu>
    </div>
  );
};

const useHiddenLayers = ({ layers, inPlayLayerIds }) => {
  const [hiddenLayers, setHiddenLayers] = useState(
    layers.map(({ id }) => id).filter((id) => !inPlayLayerIds.includes(id))
  );

  const clearHiddenLayers = () => {
    setHiddenLayers([]);
  };

  const toggleHiddenLayer = (layerId) => {
    const layer = layers.find((layer) => layer.id === layerId);

    if (!layer) {
      return;
    }

    if (hiddenLayers.includes(layerId)) {
      setHiddenLayers(hiddenLayers.filter((id) => id !== layerId));
      return;
    } else {
      setHiddenLayers([...hiddenLayers, layerId]);
      return;
    }
  };

  return { hiddenLayers, clearHiddenLayers, toggleHiddenLayer };
};

const useHighlightedLayers = ({ hiddenLayers }) => {
  const [highlightedLayers, setHighlightedLayers] = useState([]);

  const toggleHighlight = (layerId) => {
    setHighlightedLayers((prev) =>
      prev.includes(layerId)
        ? prev.filter((id) => id !== layerId)
        : [...prev, layerId]
    );
  };

  const isAnyHighlighted = highlightedLayers.length > 0;

  useEffect(() => {
    setHighlightedLayers((prev) =>
      prev.filter((layerId) => !hiddenLayers.includes(layerId))
    );
  }, [hiddenLayers]);

  return { highlightedLayers, toggleHighlight, isAnyHighlighted };
};

const TriaStatusCell = ({ layer, updateLayer, showPriorData }) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const triaStatuses = [
    {
      key: "NOT_INCLUDED",
      label: translateLabel(
        labels.ACTIVE_LAYER_PANEL_TRIA_STATUS_NOT_INCLUDED,
        {
          default: "Not Included",
        }
      ),
    },
    {
      key: "INCLUDED",
      label: translateLabel(labels.ACTIVE_LAYER_PANEL_TRIA_STATUS_INCLUDED, {
        default: "Included",
      }),
    },
    {
      key: "GROSS_UP",
      label: translateLabel(labels.ACTIVE_LAYER_PANEL_TRIA_STATUS_GROSS_UP, {
        default: "Gross Up",
      }),
    },
  ];

  const isReadOnly = showPriorData;

  if (isReadOnly) {
    const status = triaStatuses.find((s) => s.key === layer.triaStatus);
    return <Typography>{status?.label ?? layer.triaStatus}</Typography>;
  }

  return (
    <Autocomplete
      value={
        triaStatuses.find(({ key }) => key === layer?.triaStatus) ??
        triaStatuses[0]
      }
      onChange={(_, value) =>
        updateLayer(layer.id, "triaStatus", value?.key ?? null)
      }
      options={triaStatuses}
      getOptionLabel={(option) => option.label}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={"standard"}
          InputProps={{ ...params.InputProps, disableUnderline: true }}
        />
      )}
      disableClearable
      fullWidth
    />
  );
};

const LayerPricing = ({ showAdditionalInputs, setShowAdditionalInputs }) => {
  const dispatch = useDispatch();
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const priorState = useSelector(stateSelectors.selectPriorState);

  const [showPriorData, setShowPriorData] = useState(false);

  const hasPriorData = useMemo(() => {
    return (
      !_.isEmpty(priorState) &&
      priorState?.pricing?.layers &&
      Array.isArray(priorState.pricing.layers) &&
      priorState.pricing.layers.length > 0
    );
  }, [priorState]);

  const currentCalculatedValues = useSelector(analyticsStore.select)
    ?.programPricing;
  const currentLayers = useSelector(pricingSelectors.selectResolvedLayers);
  const currentProgramPricing = useSelector(programPricingSlice.select);

  const priorResolvedLayers = useMemo(() => {
    return showPriorData && priorState
      ? pricingSelectors.selectResolvedLayers(priorState)
      : [];
  }, [showPriorData, priorState]);

  const priorResolvedProgramPricing = useMemo(() => {
    if (!showPriorData || !priorState || !priorResolvedLayers.length) {
      return null;
    }
    const priorProgramPricingInput = priorState?.programPricing;
    return programPricingResolver.resolve(priorProgramPricingInput, {
      layers: priorResolvedLayers,
      SOV: priorState?.SOV,
    });
  }, [showPriorData, priorState, priorResolvedLayers]);

  const calculatedValues = showPriorData
    ? priorState?.analytics?.programPricing
    : currentCalculatedValues;

  const calculatedLayers = calculatedValues?.values?.layers ?? {};

  const layers = showPriorData ? priorResolvedLayers : currentLayers;

  const programPricing = showPriorData
    ? priorResolvedProgramPricing || {}
    : currentProgramPricing;

  const [isTransitioning, setIsTransitioning] = useState(false);

  const handleTogglePriorData = () => {
    setIsTransitioning(true);

    setShowPriorData(!showPriorData);

    setTimeout(() => setIsTransitioning(false), 300);
  };

  const sovInput = useSelector(SOVStore.selectSOV);
  const resolvedProgram = useSelector(programSelectors.selectResolvedProgram);
  const proRataFactor = resolvedProgram?.proRataAdjustment
    ? resolvedProgram.proRataAdjustment / 100
    : 1;

  const [targetPerspective, setTargetPerspective] = useState(
    dates.ProratedPerspective.PRORATED
  );

  const isVisible = useSelector(userSelectors.selectIsVisible);

  const {
    data: resolvedSov,
    isLoading: isResolvedSovFetching,
  } = sovService.useResolveSovQuery({ sovInput, geolocate: true });

  const max_tiv = useMemo(
    () =>
      resolvedSov?.locations
        ?.map(({ _TIV }) => _TIV)
        ?.reduce((max, curr) => (curr > max ? curr : max), 0) ?? null,
    [resolvedSov?.locations]
  );

  const { isFetching, refetch } = usePricing();

  const Perils = ({ layerId, programPricing, numbers }) => {
    const exposedPerils =
      programPricing?.layers?.[layerId]?.exposedPerils ?? [];

    const extractName = (peril) => {
      return programPricing?.coverageConfig?.perils?.[peril]?.name ?? peril;
    };

    const extractSublimit = (peril) => {
      return programPricing?.sublimits?.[peril] ?? null;
    };

    const extractTextSublimit = (peril) => {
      const sublimit = extractSublimit(peril);
      return numbers.abbreviated(sublimit) ?? "unlimited";
    };

    return (
      <Stack
        direction={"column"}
        spacing={0.5}
        alignItems={"center"}
        justifyContent={"center"}
        sx={{
          width: "100%",
        }}
      >
        {exposedPerils.map(({ peril, exposed, children }) => (
          <Tooltip
            key={peril}
            placement={"right"}
            title={
              <Stack direction={"column"} spacing={0.5}>
                <Typography variant={"subtitle1"}>
                  {extractName(peril)}
                </Typography>
                <Typography variant={"body1"}>{`Sublimit: ${extractTextSublimit(
                  peril
                )}`}</Typography>
                {exposed &&
                  (children ?? []).some(
                    ({ exposed: subExposed }) => !subExposed
                  ) && (
                    <>
                      <Typography variant={"body1"}>{"Excluding:"}</Typography>
                      {children
                        .filter(({ exposed }) => !exposed)
                        .map(({ peril: subPeril }) => (
                          <Typography key={subPeril} variant={"body2"}>
                            {`- ${extractName(
                              subPeril
                            )} (Sublimit: ${extractTextSublimit(subPeril)})`}
                          </Typography>
                        ))}
                    </>
                  )}
              </Stack>
            }
          >
            <Box
              sx={{
                width: "95%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: exposed
                  ? "primary.main"
                  : "secondary.lightest",
                color: exposed ? "white !important" : "black !important",
                borderRadius: "20px",
              }}
            >
              {peril}
            </Box>
          </Tooltip>
        ))}
      </Stack>
    );
  };

  const inPlayLayerIds = layerSupport.extractInPlayLayerIds(layers);

  const {
    hiddenLayers,
    clearHiddenLayers,
    toggleHiddenLayer,
  } = useHiddenLayers({
    layers,
    inPlayLayerIds,
  });

  const {
    highlightedLayers,
    isAnyHighlighted,
    toggleHighlight,
  } = useHighlightedLayers({
    hiddenLayers,
  });

  const analytics = useSelector(analyticsStore.select);
  const { data: analyticsConfig } = staticDataService.useConfigQuery(
    "analytics"
  );
  const { data: reinsurersConfig } = staticDataService.useConfigQuery(
    "reinsurers"
  );
  const { data: policyFormsConfig } = staticDataService.useConfigQuery(
    "policy_forms"
  );
  const { data: catExposuresConfig } = staticDataService.useConfigQuery(
    "cat_exposures"
  );

  const currentEngine =
    programPricing?.engine ??
    analytics?.programPricing?.engine ??
    analyticsConfig?.programPricing?.engine;
  const [
    changePricingModelDialogOpen,
    setChangePricingModelDialogOpen,
  ] = useState(false);

  const handlePricingModelChange = (newEngine) => {
    dispatch(
      programPricingSlice.update({
        engine: newEngine,
      })
    );
    refetch();
    setChangePricingModelDialogOpen(false);
  };

  const policyFormMapping = Object.fromEntries(
    (
      policyFormsConfig?.policy_forms ?? config.POLICY_FORMS
    ).map(({ code, name }) => [code, name])
  );

  // Use the shared RPI calculation hook
  const { validation: rpiValidation } = useRpiCalculation();

  const fields = [
    {
      key: "paperProvider",
      label: translateLabel(labels.LAYER_PRICING_TABLE_PAPER_PROVIDER_LABEL, {
        default: "Paper Provider",
      }),
      root: "layer",
      visibilityTag: "<ActiveLayerEditor>.reinsurance",
      defaultVisible: false,
      renderCell: ({ layer }) => {
        const value = layer.paperProvider;
        const reinsurers = reinsurersConfig?.reinsurers ?? [];
        // The sentinel has its own menu item; keep it out of the
        // unknown-stored-value fallback below or the menu gets a duplicate.
        const valueExistsInList = value
          ? value === NONE_PAPER_PROVIDER ||
            reinsurers.some((reinsurer) => reinsurer.key === value)
          : false;
        const displayName =
          value && !valueExistsInList
            ? reinsurers.find((r) => r.key === value)?.name ??
              value ??
              "<Unknown>"
            : null;

        return (
          <Disabled ifReadOnly>
            <FormControl variant={"filled"} fullWidth size={"small"}>
              <Select
                value={value ?? "__NULL__"}
                onChange={(e) => {
                  if (showPriorData) return;
                  let paperProvider = e.target.value;
                  if (paperProvider === "__NULL__") {
                    paperProvider = null;
                  }
                  updateLayer(layer.id, "paperProvider", paperProvider);
                }}
                sx={{
                  "& .MuiSelect-select": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                }}
              >
                <MenuItem value={"__NULL__"}>{"Default"}</MenuItem>
                <MenuItem value={NONE_PAPER_PROVIDER}>{"None"}</MenuItem>
                {value && !valueExistsInList && (
                  <MenuItem value={value}>{displayName}</MenuItem>
                )}
                {reinsurers.map((reinsurer) => (
                  <MenuItem key={reinsurer.key} value={reinsurer.key}>
                    {reinsurer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Disabled>
        );
      },
      visible: isVisible("<ActiveLayerEditor>.reinsurance") ?? false,
    },
    {
      key: "rpi",
      label: translateLabel(labels.LAYER_PRICING_TABLE_RPI_LABEL, {
        default: "RPI",
      }),
      renderCell: ({ layer }) => {
        return <RpiCell layerId={layer.id} />;
      },
      visible: rpiValidation.shouldShow,
    },
    {
      key: "catModel",
      label: translateLabel(labels.LAYER_PRICING_TABLE_CAT_MODEL_LABEL, {
        default: "Cat Model",
      }),
      root: "programPricing",
      renderCell: ({ layer }) => {
        const catModelNames = Object.fromEntries(
          (catExposuresConfig?.enums?.catModels?.choices ?? [])
            .filter(({ key, name }) => !!key && !!name)
            .map(({ key, name }) => [key, name])
        );
        const value =
          programPricing?.layers?.[layer.id]?.catModel ??
          calculatedLayers?.[layer.id]?.catModel;

        return (
          <Disabled ifReadOnly>
            <FormControl variant={"filled"} fullWidth size={"small"}>
              <Select
                value={value ?? "__NULL__"}
                onChange={(e) => {
                  if (showPriorData) return;
                  let catModel = e.target.value;
                  if (catModel === "__NULL__") {
                    catModel = null;
                  }
                  updateProgramPricing(layer.id, "catModel", catModel);
                }}
                sx={{
                  "& .MuiSelect-select": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                }}
              >
                {!value && <MenuItem value={"__NULL__"}>{"---"}</MenuItem>}
                {(calculatedValues?.values?.catModels ?? []).map((key) => (
                  <MenuItem key={key} value={key}>
                    {catModelNames?.[key] ?? key}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Disabled>
        );
      },
      visible: calculatedValues?.values?.catModels?.length,
    },
    {
      key: "nonCatScale",
      label: translateLabel(labels.LAYER_PRICING_TABLE_NON_CAT_SCALE_LABEL, {
        default: "Non-Cat Scale",
      }),
      root: "programPricing",
      renderCell: ({ layer }) => {
        const value = programPricing?.layers?.[layer.id]?.nonCatScale ?? null;
        return (
          <Disabled ifReadOnly>
            <FormControl variant={"filled"} fullWidth size={"small"}>
              <Select
                value={value ?? "__NULL__"}
                onChange={(e) => {
                  if (showPriorData) return;
                  let nonCatScale = e.target.value;
                  if (nonCatScale === "__NULL__") {
                    nonCatScale = null;
                  }
                  updateProgramPricing(layer.id, "nonCatScale", nonCatScale);
                }}
                sx={{
                  "& .MuiSelect-select": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                }}
              >
                <MenuItem value={"__NULL__"}>{"Default"}</MenuItem>
                <MenuItem value={NONE_PAPER_PROVIDER}>{"None"}</MenuItem>
                <MenuItem value={"A"}>{"A Scale"}</MenuItem>
                <MenuItem value={"B"}>{"B Scale"}</MenuItem>
                <MenuItem value={"C"}>{"C Scale"}</MenuItem>
                <MenuItem value={"D"}>{"D Scale"}</MenuItem>
                <MenuItem value={"N"}>{"N Scale"}</MenuItem>
                <MenuItem value={"LDN_Low"}>{"LDN Low"}</MenuItem>
                <MenuItem value={"LDN_Normal"}>{"LDN Normal"}</MenuItem>
                <MenuItem value={"LDN_High"}>{"LDN High"}</MenuItem>
                <MenuItem value={"LDN_Excess"}>{"LDN Excess"}</MenuItem>
              </Select>
            </FormControl>
          </Disabled>
        );
      },
      visible: true,
    },
    {
      key: "nonCatTechnical",
      perspective: "annualized",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_NON_CAT_TECHNICAL_LABEL,
        {
          default: "Non-Cat Technical",
        }
      ),
      root: "layerPricing",
      valueType: "currency",
      inputType: "number",
      readOnly: true,
      visible: true,
    },
    {
      key: "catScale",
      label: translateLabel(labels.LAYER_PRICING_TABLE_CAT_SCALE_LABEL, {
        default: "Cat Scale",
      }),
      root: "programPricing",
      renderCell: ({ layer }) => {
        const value = programPricing?.layers?.[layer.id]?.catScale ?? null;
        return (
          <Disabled ifReadOnly>
            <FormControl variant={"filled"} fullWidth size={"small"}>
              <Select
                value={value ?? "__NULL__"}
                onChange={(e) => {
                  if (showPriorData) return;
                  let catScale = e.target.value;
                  if (catScale === "__NULL__") {
                    catScale = null;
                  }
                  updateProgramPricing(layer.id, "catScale", catScale);
                }}
                sx={{
                  "& .MuiSelect-select": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                }}
              >
                <MenuItem value={"__NULL__"}>{"Default"}</MenuItem>
                <MenuItem value={NONE_PAPER_PROVIDER}>{"None"}</MenuItem>
                <MenuItem value={"C"}>{"C Scale"}</MenuItem>
                <MenuItem value={"MODEL"}>{"Modelling"}</MenuItem>
                <MenuItem value={"CAT"}>{"Cat Scale"}</MenuItem>
                <Divider />
                <MenuItem value={"LDN_Low"}>{"LDN Low"}</MenuItem>
                <MenuItem value={"LDN_Normal"}>{"LDN Normal"}</MenuItem>
                <MenuItem value={"LDN_High"}>{"LDN High"}</MenuItem>
                <MenuItem value={"LDN_Excess"}>{"LDN Excess"}</MenuItem>
              </Select>
            </FormControl>
          </Disabled>
        );
      },
      visible: true,
    },
    {
      key: "catTechnicalWeighting",
      label: "Cat Split",
      renderCell: ({ layer }) => {
        const sliderVisible = (
          programPricing?.layers?.[layer.id]?.catScale ?? ""
        ).startsWith("LDN_");
        if (!sliderVisible) {
          return <></>;
        }

        const value = programPricing?.layers?.[layer.id]?.catTechnicalWeighting;
        const hasModelData =
          calculatedLayers?.[layer.id]?.catTechnicalMODEL != null;
        const needsModelData = value != null && value !== 100;
        const showModelDataWarning = needsModelData && !hasModelData;

        return (
          <Stack gap={1}>
            <FormControl variant={"filled"} fullWidth size={"small"}>
              <InputLabel>{"Underwriter"}</InputLabel>
              <Select
                value={value}
                onChange={(e) => {
                  updateProgramPricing(
                    layer.id,
                    "catTechnicalWeighting",
                    e.target.value
                  );
                }}
                size={"small"}
              >
                <MenuItem value={0}>{"0"}</MenuItem>
                <MenuItem value={10}>{"10"}</MenuItem>
                <MenuItem value={20}>{"20"}</MenuItem>
                <MenuItem value={30}>{"30"}</MenuItem>
                <MenuItem value={40}>{"40"}</MenuItem>
                <MenuItem value={50}>{"50"}</MenuItem>
                <MenuItem value={60}>{"60"}</MenuItem>
                <MenuItem value={70}>{"70"}</MenuItem>
                <MenuItem value={80}>{"80"}</MenuItem>
                <MenuItem value={90}>{"90"}</MenuItem>
                <MenuItem value={100}>{"100"}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={"Model"}
              variant={"filled"}
              size={"small"}
              value={100 - value}
              disabled
              error={showModelDataWarning}
              sx={{ input: { textAlign: "right" } }}
            />
            {showModelDataWarning && (
              <Typography
                variant={"caption"}
                color={"error"}
                sx={{ textAlign: "center" }}
              >
                {translateLabel(
                  labels.LAYER_PRICING_TABLE_CAT_SPLIT_NO_MODEL_DATA_ERROR,
                  {
                    default:
                      "No model data. Set Underwriter to 100% or load model data.",
                  }
                )}
              </Typography>
            )}
          </Stack>
        );
      },
      visible: Object.values(programPricing?.layers || {}).some((layer) =>
        layer?.catScale?.startsWith?.("LDN_")
      ),
    },
    {
      key: "catTechnical",
      perspective: "annualized",
      label: translateLabel(labels.LAYER_PRICING_TABLE_CAT_TECHNICAL_LABEL, {
        default: "Cat Technical",
      }),
      root: "layerPricing",
      valueType: "currency",
      readOnly: true,
      visible: true,
    },
    {
      key: "technicalPremium",
      perspective: "annualized",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_TECHNICAL_PREMIUM_LABEL,
        {
          default: "Technical",
        }
      ),
      root: "layerPricing",
      valueType: "currency",
      readOnly: true,
      visible: true,
    },
    {
      key: "ROL",
      label: translateLabel(labels.LAYER_PRICING_TABLE_ROL_LABEL, {
        default: "ROL",
      }),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "brokerTargetGrossPremium",
      label: translateLabel(labels.LAYER_PRICING_TABLE_BROKER_TARGET_LABEL, {
        default: "Broker Target",
      }),
      perspective: "prorated",
      root: "layer",
      valueType: "currency",
      onChange: "updateLayer",
      readOnly: showPriorData,
      visible: true,
    },
    {
      key: "ROLtarget",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_BROKER_TARGET_ROL_LABEL,
        {
          default: "Broker Target ROL",
        }
      ),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "quotedGrossPremium",
      perspective: "prorated",
      root: "layer",
      label: translateLabel(labels.LAYER_PRICING_TABLE_HELIX_QUOTE_LABEL, {
        default: "Helix Quote",
      }),
      onChange: "updateLayer",
      valueType: "currency",
      readOnly: showPriorData,
      visible: true,
    },
    {
      key: "ROLquote",
      label: translateLabel(labels.LAYER_PRICING_TABLE_HELIX_QUOTE_ROL_LABEL, {
        default: "Helix Quote ROL",
      }),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "grossPremium",
      perspective: "prorated",
      root: "layer",
      label: translateLabel(labels.LAYER_PRICING_TABLE_FOT_LABEL, {
        default: "FOT",
      }),
      onChange: "updateLayer",
      valueType: "currency",
      readOnly: showPriorData,
      visible: true,
    },
    {
      key: "ROLFOT",
      root: "layerPricing",
      label: translateLabel(labels.LAYER_PRICING_TABLE_FOT_ROL_LABEL, {
        default: "FOT ROL",
      }),
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "GG_ULR_COMBINED",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_GROSS_LR_OF_TECHNICAL_PREMIUM_LABEL,
        {
          default: "Gross LR of Technical Premium",
        }
      ),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "percentageTechnicalHelixQuote",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_HELIX_QUOTE_AS_PCT_OF_TECHNICAL_LABEL,
        {
          default: "Helix Quote as % of Technical",
        }
      ),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "percentageTechnicalFOT",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_FOT_AS_PCT_OF_TECHNICAL_LABEL,
        {
          default: "FOT as % of Technical",
        }
      ),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "GGLRQuote",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_GROSS_GROSS_LOSS_RATIO_QUOTE_LABEL,
        {
          default: "Gross Gross Loss Ratio - Quote",
        }
      ),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "GGLRFOT",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_GROSS_GROSS_LOSS_RATIO_FOT_LABEL,
        {
          default: "Gross Gross Loss Ratio - FOT",
        }
      ),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "GGLRCombinedRatioQuote",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_COMBINED_UW_RATIO_QUOTE_LABEL,
        {
          default: "Combined UW Ratio - Quote",
        }
      ),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "GGLRCombinedRatioFOT",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_COMBINED_UW_RATIO_FOT_LABEL,
        {
          default: "Combined UW Ratio - FOT",
        }
      ),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "quotedLineSize",
      perspective: "prorated",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_EXPOSURE_LINE_OUR_SHARE_QUOTE_LABEL,
        {
          default: "Exposure/Line Our Share - Quote",
        }
      ),
      root: "layer",
      valueType: "currency",
      onChange: "updateLayer",
      readOnly: showPriorData,
      visible: true,
      invariantToPerspective: true,
    },
    {
      key: "lineSize",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_EXPOSURE_LINE_OUR_SHARE_FOT_LABEL,
        {
          default: "Exposure/Line Our Share - FOT",
        }
      ),
      root: "layer",
      valueType: "currency",
      onChange: "updateLayer",
      readOnly: showPriorData,
      visible: true,
      invariantToPerspective: true,
    },
    {
      key: "quotedShareOfPremium",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_GROSS_PREMIUM_OUR_SHARE_QUOTE_LABEL,
        {
          default: "Gross Premium Our Share - Quote",
        }
      ),
      root: "layer",
      valueType: "currency",
      readOnly: true,
      visible: true,
    },
    {
      key: "shareOfPremium",
      label: translateLabel(
        labels.LAYER_PRICING_TABLE_GROSS_PREMIUM_OUR_SHARE_FOT_LABEL,
        {
          default: "Gross Premium Our Share - FOT",
        }
      ),
      root: "layer",
      valueType: "currency",
      readOnly: true,
      visible: true,
    },
    {
      key: "triaStatus",
      label: translateLabel(labels.LAYER_PRICING_TABLE_TRIA_STATUS_LABEL, {
        default: "TRIA Status",
      }),
      renderCell: ({ layer }) => (
        <TriaStatusCell
          layer={layer}
          updateLayer={updateLayer}
          showPriorData={showPriorData}
        />
      ),
      visible: true,
    },
    {
      key: "TRIA",
      label: translateLabel(labels.LAYER_PRICING_TABLE_TRIA_PCT_LABEL, {
        default: "TRIA %",
      }),
      root: "layer",
      valueType: "percentage",
      onChange: "updateLayer",
      readOnly: showPriorData,
      visible: true,
      invariantToPerspective: true,
    },
    {
      key: "TRIAAmount",
      label: translateLabel(labels.LAYER_PRICING_TABLE_TRIA_AMOUNT_LABEL, {
        default: "TRIA Amount",
      }),
      root: "layer",
      valueType: "currency",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      // The followed policy is a pointer from this layer to a tower share, so
      // it sits at the foot of the table rather than among the pricing rows.
      key: "followedPolicyTowerShareId",
      label: translateLabel(labels.LAYER_PRICING_TABLE_FOLLOWED_POLICY_LABEL, {
        default: "Followed Policy",
      }),
      root: "layer",
      visibilityTag: FOLLOWED_POLICY_TAG,
      defaultVisible: false,
      renderCell: ({ layer }) => (
        <Disabled ifReadOnly>
          <FollowedPolicyTableSelect layer={layer} disabled={showPriorData} />
        </Disabled>
      ),
      visible: isVisible(FOLLOWED_POLICY_TAG) ?? false,
    },
  ].filter(
    ({ key }) => isVisible(`<DnfLayerPricing>.layerPricing.${key}`) ?? true
  );

  const fieldData = buildDataFromFields(
    fields,
    layers,
    calculatedValues,
    programPricing
  );

  const updateProgramPricing = (layerId, key, value) => {
    if (showPriorData) return;

    const updatedLayers = programPricing?.layers ?? {};
    const currentLayer = updatedLayers[layerId] ?? {};

    const fieldConfig = fields.find((f) => f.key === key);
    const fieldPerspective =
      fieldConfig?.perspective || dates.ProratedPerspective.PRORATED;

    const updatedValue = dates.prorate(value, {
      originalPerspective: targetPerspective,
      targetPerspective: fieldPerspective,
      proRataFactor,
    });

    dispatch(
      programPricingSlice.update({
        layers: {
          ...updatedLayers,
          [layerId]: {
            ...currentLayer,
            [key]: updatedValue,
          },
        },
      })
    );
    refetch();
  };

  const updateLayer = (layerId, key, value) => {
    if (showPriorData) return;

    const values = {
      [key]: value,
    };
    if (key === "lineSize") {
      values.shareOfLine = null;
    }

    const fieldConfig = fields.find((f) => f.key === key);
    const fieldPerspective =
      fieldConfig?.perspective || dates.ProratedPerspective.PRORATED;

    if (!fieldConfig?.invariantToPerspective) {
      values[key] = dates.prorate(value, {
        originalPerspective: targetPerspective,
        targetPerspective: fieldPerspective,
        proRataFactor,
      });
    }

    dispatch(
      pricingActions.updateLayer({
        layerId: layerId,
        values,
      })
    );
    refetch();
  };

  const renderCell = (layer, field) => {
    if (field.renderCell) {
      return field.renderCell({ layer });
    }

    const value = fieldData[layer.id]?.[field.key] ?? null;
    const hasValue = value != null && value !== "";

    const fieldPerspective =
      field.perspective || dates.ProratedPerspective.PRORATED;

    const displayValue =
      hasValue && !field.invariantToPerspective
        ? dates.prorate(value, {
            originalPerspective: fieldPerspective,
            targetPerspective: targetPerspective,
            proRataFactor,
          })
        : value;

    const altPerspective =
      targetPerspective === dates.ProratedPerspective.PRORATED
        ? dates.ProratedPerspective.ANNUALIZED
        : dates.ProratedPerspective.PRORATED;

    const helperValue =
      hasValue && !field.invariantToPerspective
        ? dates.prorate(value, {
            originalPerspective: fieldPerspective,
            targetPerspective: altPerspective,
            proRataFactor,
          })
        : null;

    const altPerspectiveLabel =
      targetPerspective === dates.ProratedPerspective.PRORATED
        ? "Annualized: "
        : "Pro Rated: ";

    const helperText =
      helperValue != null && proRataFactor !== 1
        ? `${altPerspectiveLabel}${numbers.currencyFormat(helperValue)}`
        : null;

    if (field.readOnly && !field.onChange) {
      if (!hasValue) {
        return <></>;
      } else if (field.valueType === "percentage") {
        return (
          <Tooltip title={displayValue}>
            <Typography>
              {numbers.percentage(field.dp ?? 3)(displayValue)}
            </Typography>
          </Tooltip>
        );
      } else if (field.valueType === "currency") {
        return (
          <Box>
            <Typography>
              {numbers.currencyFormat(displayValue, field.dp ?? 0)}
            </Typography>
            {helperText && (
              <Typography
                variant={"caption"}
                color={"text.secondary"}
                sx={{
                  display: "block",
                  textAlign: "right",
                }}
              >
                {helperText}
              </Typography>
            )}
          </Box>
        );
      } else {
        return (
          <Tooltip title={displayValue}>
            <Typography>
              {numbers.dpString(field.dp ?? 0)(displayValue)}
            </Typography>
          </Tooltip>
        );
      }
    }
    const isReadOnly = showPriorData || field.readOnly;

    return (
      <Disabled ifReadOnly>
        <PrettyNumberTextField
          type={"text"}
          size={"small"}
          disabled={isReadOnly}
          hiddenLabel
          suffix={field.valueType === "percentage" ? "%" : ""}
          prefix={field.valueType === "currency" ? "$" : ""}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            style: {
              textAlign: "right",
            },
          }}
          value={displayValue}
          onChangeNumber={
            field.onChange === "updateLayer"
              ? (value) => updateLayer(layer.id, field.key, value)
              : (value) => updateProgramPricing(layer.id, field.key, value)
          }
          variant={"filled"}
          helperText={helperText}
        />
      </Disabled>
    );
  };

  const maxTivPercentages = useMemo(() => {
    const makeLayer = (percentage) => ({
      prefix: "As % of top loc:",
      value: numbers.percentage(0)(percentage),
    });

    return layers.reduce((acc, layer) => {
      const attachmentPctOfMaxLocationTiv =
        calculatedValues?.values?.layers?.[layer.id]
          ?.attachmentPctOfMaxLocationTiv;
      if (attachmentPctOfMaxLocationTiv != null) {
        acc[layer.id] = makeLayer(attachmentPctOfMaxLocationTiv / 100);
        return acc;
      }
      if (isResolvedSovFetching) {
        acc[layer.id] = "Loading...";
      } else if (!max_tiv) {
        acc[layer.id] = "No SOV data";
      } else if (!layer?.attachment) {
        acc[layer.id] = "No attachment point";
      } else {
        acc[layer.id] = makeLayer(layer.attachment / max_tiv);
      }
      return acc;
    }, {});
  }, [layers, max_tiv, isResolvedSovFetching, calculatedValues]);

  const MaxTivAttachmentPercentage = ({ percentage }) => {
    if (typeof percentage === "string") {
      return <Typography fontSize={"0.8rem"}>{percentage}</Typography>;
    }

    return (
      <Box sx={{ display: "inline-flex" }}>
        <Typography fontSize={"0.8rem"}>{percentage.prefix}</Typography>
        <Typography
          fontSize={"0.8rem"}
          sx={{ fontWeight: "bold", marginLeft: "0.2rem" }}
        >
          {percentage.value}
        </Typography>
      </Box>
    );
  };

  const renderRow = (field, index) => {
    const rowContent = (
      <TableRow key={index}>
        <TableCell align={"right"} sx={{ minWidth: "200px" }}>
          <Typography fontSize={"0.7rem"}>{field.label}</Typography>
        </TableCell>
        <TransitionGroup component={null}>
          {layers
            .filter((layer) => !hiddenLayers.includes(layer.id))
            .map((layer, index) => (
              <Fade
                key={index}
                timeout={{ enter: FADE_TIMEOUT, exit: 0 }}
                appear={false}
              >
                <TableCell
                  key={`row-${field.key}-column-${layer.id}`}
                  align={"right"}
                  style={{
                    ...getCellStyle(
                      layer.id,
                      highlightedLayers,
                      isAnyHighlighted
                    ),
                  }}
                >
                  <Box
                    sx={{
                      transition: "opacity 300ms ease-in-out",
                      opacity: isTransitioning ? 0.3 : 1,
                    }}
                  >
                    {renderCell(layer, field)}
                  </Box>
                </TableCell>
              </Fade>
            ))}
        </TransitionGroup>
        <TableCell>{/* Extra space */}</TableCell>
      </TableRow>
    );

    if (field.visibilityTag) {
      return (
        <Visible
          byTag={field.visibilityTag}
          defaultVisible={field.defaultVisible}
          key={index}
        >
          {rowContent}
        </Visible>
      );
    }

    return rowContent;
  };

  return (
    <Component
      title={"Summary"}
      isFetching={isFetching}
      options={
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          {proRataFactor !== 1 && (
            <FormControlLabel
              control={
                <Switch
                  checked={
                    targetPerspective === dates.ProratedPerspective.PRORATED
                  }
                  onChange={(e) =>
                    setTargetPerspective(
                      e.target.checked
                        ? dates.ProratedPerspective.PRORATED
                        : dates.ProratedPerspective.ANNUALIZED
                    )
                  }
                  color={"primary"}
                />
              }
              label={
                <Typography fontSize={"0.8rem"}>
                  {targetPerspective === dates.ProratedPerspective.PRORATED
                    ? "Pro Rated"
                    : "Annualized"}
                </Typography>
              }
            />
          )}
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <MuiButton
              onClick={() => clearHiddenLayers()}
              variant={"text"}
              disabled={hiddenLayers.length === 0}
              sx={{
                m: 1,
                display: "flex",
                width: "150px",
                justifyContent: "flex-end",
                padding: "0 16px",
              }}
              endIcon={
                <VisibilityIcon
                  size={"m"}
                  color={hiddenLayers.length > 0 ? "primary" : "disabled"}
                />
              }
            >
              {hiddenLayers.length > 0 && (
                <Typography fontSize={"0.8rem"} sx={{ mr: 1 }}>
                  {hiddenLayers.length} {"hidden"}
                </Typography>
              )}
            </MuiButton>
          </Stack>
          <Disabled ifReadOnly>
            <Button onClick={refetch} isLoading={isFetching}>
              {"Calculate"}
            </Button>
          </Disabled>
          {hasPriorData && (
            <Tooltip
              title={showPriorData ? "Show Current Data" : "Show Prior Data"}
            >
              <IconButton
                onClick={handleTogglePriorData}
                color={showPriorData ? "primary" : "default"}
                size={"small"}
              >
                <PriorDataIcon />
              </IconButton>
            </Tooltip>
          )}
          <Visible byTag={"<DnfLayerPricing>.layerPricing.menu"} defaultVisible>
            <ActionMenu
              showAdditionalInputs={showAdditionalInputs}
              setShowAdditionalInputs={setShowAdditionalInputs}
              onOpenChangePricingModel={() =>
                setChangePricingModelDialogOpen(true)
              }
            />
          </Visible>
        </Stack>
      }
    >
      <Stack direction={"column"} spacing={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align={"right"} width={200}></TableCell>
                <TransitionGroup component={null}>
                  {layers
                    .filter((layer) => !hiddenLayers.includes(layer.id))
                    .map((layer) => (
                      <Fade
                        key={layer.id}
                        timeout={{ enter: FADE_TIMEOUT, exit: 0 }}
                        appear={false}
                      >
                        <TableCell
                          key={`column-${layer.id}`}
                          align={"right"}
                          width={200}
                          sx={{
                            mt: 1,
                            "&:hover .hover-icons": {
                              visibility: "visible",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              transition: "opacity 300ms ease-in-out",
                              opacity: isTransitioning ? 0.3 : 1,
                            }}
                          >
                            <Stack direction={"column"} spacing={0}>
                              <Stack
                                direction={"row"}
                                spacing={0}
                                justifyContent={"center"}
                                alignItems={"center"}
                                sx={{
                                  ml: 1,
                                  minHeight: "40px",
                                }}
                              >
                                <IconButton
                                  onClick={() => toggleHighlight(layer.id)}
                                  className={"hover-icons"}
                                  sx={{
                                    ml: 1,
                                    visibility: "hidden",
                                  }}
                                >
                                  <StarIcon />
                                </IconButton>
                                <IconButton
                                  onClick={() => toggleHiddenLayer(layer.id)}
                                  className={"hover-icons"}
                                  sx={{
                                    ml: 1,
                                    visibility: "hidden",
                                  }}
                                >
                                  <VisibilityOffIcon />
                                </IconButton>
                              </Stack>
                              <Stack
                                direction={"row"}
                                spacing={1}
                                justifyContent={"center"}
                                alignItems={"center"}
                              >
                                <Visible
                                  byTag={
                                    "pricing.dnfLayerPricing.topLocTIVPercentage"
                                  }
                                  defaultVisible={true}
                                >
                                  <MaxTivAttachmentPercentage
                                    percentage={maxTivPercentages[layer.id]}
                                  />
                                </Visible>
                              </Stack>
                              <Stack
                                direction={"row"}
                                spacing={1}
                                justifyContent={"center"}
                                alignItems={"center"}
                              >
                                <Visible
                                  byTag={"pricing.dnfLayerPricing.policyForm"}
                                  defaultVisible={true}
                                >
                                  <Typography
                                    fontSize={"0.8rem"}
                                    sx={{
                                      backgroundColor: "#a6caec",
                                      color: "white",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                    }}
                                  >
                                    {policyFormMapping[layer?.policyForm] ??
                                      getPolicyFormName(layer?.policyForm) ??
                                      null}
                                  </Typography>
                                </Visible>
                              </Stack>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <Tooltip title={layer.status}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <StatusBadgeV2
                                        status={layer.status}
                                        variant={"icon"}
                                      />
                                    </Box>
                                    <Typography
                                      fontSize={"0.8rem"}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {utils.shortLayerName(layer)}
                                    </Typography>
                                    <Visible
                                      byTag={
                                        "pricing.dnfLayerPricing.statusSelector"
                                      }
                                      defaultVisible={true}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <StatusSelect
                                          layer={layer}
                                          variant={"switch"}
                                          status={layer?.status ?? null}
                                          onChange={(status) => {
                                            dispatch(
                                              pricingActions.updateLayer({
                                                layerId: layer.id,
                                                values: { status },
                                              })
                                            );
                                          }}
                                        />
                                      </Box>
                                    </Visible>
                                  </Box>
                                </Tooltip>
                              </Box>
                              {/* Error Badge with Tooltip */}
                              {calculatedLayers[layer.id]?.error &&
                                calculatedLayers[layer.id].error.length > 0 && (
                                  <Stack
                                    direction={"row"}
                                    spacing={1}
                                    alignItems={"center"}
                                    justifyContent={"center"}
                                  >
                                    <Tooltip
                                      title={calculatedLayers[layer.id].error
                                        .map((error) => error.message)
                                        .join(", ")}
                                    >
                                      <Badge
                                        badgeContent={
                                          calculatedLayers[layer.id].error
                                            .length
                                        }
                                        color={"error"}
                                      >
                                        <ErrorIcon
                                          variant={"outlined"}
                                          color={"error"}
                                        />
                                      </Badge>
                                    </Tooltip>
                                  </Stack>
                                )}
                            </Stack>
                          </Box>
                        </TableCell>
                      </Fade>
                    ))}
                </TransitionGroup>
                <TableCell>{/* Extra space */}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align={"right"} sx={{ minWidth: "200px" }}>
                  <Typography fontSize={"0.7rem"}>
                    {translateLabel(labels.LAYER_PRICING_TABLE_PERILS_LABEL, {
                      default: "Perils",
                    })}
                  </Typography>
                </TableCell>
                <TransitionGroup component={null}>
                  {layers
                    .filter((layer) => !hiddenLayers.includes(layer.id))
                    .map((layer, index) => (
                      <Fade
                        key={index}
                        timeout={{ enter: FADE_TIMEOUT, exit: 0 }}
                        appear={false}
                      >
                        <TableCell
                          key={`perils-column-${layer.id}`}
                          align={"right"}
                          style={getCellStyle(
                            layer.id,
                            highlightedLayers,
                            isAnyHighlighted
                          )}
                        >
                          <Box
                            sx={{
                              transition: "opacity 300ms ease-in-out",
                              opacity: isTransitioning ? 0.3 : 1,
                            }}
                          >
                            <Perils
                              layerId={layer.id}
                              programPricing={programPricing}
                              numbers={numbers}
                            />
                          </Box>
                        </TableCell>
                      </Fade>
                    ))}
                </TransitionGroup>
                <TableCell>{/* Extra space */}</TableCell>
              </TableRow>
              {fields
                .filter((field) => field.visible)
                .map((field, index) => renderRow(field, index))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
      <ChangePricingModelDialog
        open={changePricingModelDialogOpen}
        onClose={() => setChangePricingModelDialogOpen(false)}
        onModelChange={handlePricingModelChange}
        value={currentEngine}
        engineConfig={analyticsConfig?.engineConfig}
      />
    </Component>
  );
};

export default LayerPricing;
